import { useCallback, useEffect, useRef } from 'react';

export interface UseDebouncedSaveOptions<TValue> {
    /** Called with the most recent value once `delayMs` has passed without a further `schedule` call. */
    onSave: (value: TValue) => void;
    delayMs: number;
}

export interface UseDebouncedSaveResult<TValue> {
    /** Restarts the delay with this value, replacing any pending call. */
    schedule: (value: TValue) => void;
}

/**
 * Debounces a save: each `schedule` call restarts the delay, so only the last value within a burst
 * of edits is ever saved. Unmounting **flushes** rather than cancels — an `EmployeeRow` unmounts
 * as soon as the reader pages, searches, sorts or switches view, and a score typed a fraction of a
 * second earlier must not disappear with it.
 */
export const useDebouncedSave = <TValue>({
    onSave,
    delayMs,
}: UseDebouncedSaveOptions<TValue>): UseDebouncedSaveResult<TValue> => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingValueRef = useRef<{ value: TValue } | null>(null);

    // Read through a ref so a `schedule` closure created before an `onSave` identity change
    // still calls the latest version, without resetting the pending delay for that unrelated
    // reason.
    const onSaveRef = useRef(onSave);
    onSaveRef.current = onSave;

    const clearPendingTimeout = useCallback(() => {
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const flush = useCallback(() => {
        const pending = pendingValueRef.current;
        clearPendingTimeout();
        pendingValueRef.current = null;

        if (pending) {
            onSaveRef.current(pending.value);
        }
    }, [clearPendingTimeout]);

    const schedule = useCallback(
        (value: TValue) => {
            clearPendingTimeout();
            pendingValueRef.current = { value };
            timeoutRef.current = setTimeout(() => {
                timeoutRef.current = null;
                pendingValueRef.current = null;
                onSaveRef.current(value);
            }, delayMs);
        },
        [clearPendingTimeout, delayMs],
    );

    useEffect(() => flush, [flush]);

    return { schedule };
};
